import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { Service } from 'rainbow-web-sdk/services/service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { BUBBLE_CONFERENCE_SVC, BubbleConferenceServiceEvents } from 'rainbow-web-sdk/services/conference/bubbleConference.service';
import { ProfileService } from 'rainbow-web-sdk/services/core';
import { BubbleConferenceStatus } from '../../models/webConferenceSession.model';

export interface ISpeechEnhancementService {
    startProcessStream(id: string, stream: MediaStream, serviceName?: string): Promise<MediaStream>;
    stopProcessStream(id: string, serviceName?: string): Promise<void>;
}

export const SPEECH_ENH_SVC = 'SpeechEnhancementService';

interface ActiveSpeechEnhancementInterface {
    stream?: MediaStream;
    context?: AudioContext;
    streamDestination?: MediaStreamAudioDestinationNode;
    speechEnhancementNode?: AudioWorkletNode;
    preGainNode?: GainNode;
    postGainNode?: GainNode;
    filterNode?: BiquadFilterNode;
    source?: MediaStreamAudioSourceNode;
    originalTrack?: MediaStreamTrack;
}

export class SpeechEnhancementService extends Service implements ISpeechEnhancementService {

    public static getInstance() { return Service.create(SPEECH_ENH_SVC, SpeechEnhancementService); }
    public static build() { return new SpeechEnhancementService(); }
    private constructor() { super(); }

    private logger: LoggerService = LoggerService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private webConferenceService: any = null;

    private stream: MediaStream;
    private context: AudioContext;
    private streamDestination: MediaStreamAudioDestinationNode;
    private speechEnhancementNode: AudioWorkletNode;
    private preGainNode: GainNode;
    private postGainNode: GainNode;
    private filterNode: BiquadFilterNode;
    private workletActivated = false;
    private source: MediaStreamAudioSourceNode;
    private originalTrack: MediaStreamTrack;

    private activeSpeechEnhancements = {};

    /**
     * @function startProcessStream
     * @instance
     * @public
     * @async
     * @description Start the effect of speech enhancement
     * @param {string} id the ID that will be releated to the speech enchacement object containing the streams
     * @param {MediaStream} stream The MediaStream  that we will process
     * @param {string} serviceName The name of service that calls the method (optional, for debug purpose)
     * @returns {Promise<MediaStream>} the new media stream
     */
    public async startProcessStream(id: string, stream: MediaStream, serviceName?: string): Promise<MediaStream> {
        //if we've already an active speech enchacement with same id, stop it first and start a new one
        if (this.activeSpeechEnhancements[id]) {
            await this.stopProcessStream(id);
        }

        const newActiveSpeechEnhancement: ActiveSpeechEnhancementInterface = {};
        newActiveSpeechEnhancement.stream = stream;
        const currentAudioSetting = this.settingsService.getSetting('speechEnhancementType');

        if (currentAudioSetting !== 'none') {

            this.logger.info(`[speechEnhancementService] start noise reduction from service ${serviceName ? serviceName : 'Unknown'} and ID ${id}`);

            //save the track to be able to stop it later or to be able to insert it in case of error
            if (newActiveSpeechEnhancement.stream.getAudioTracks().length > 0) { newActiveSpeechEnhancement.originalTrack = newActiveSpeechEnhancement.stream.getAudioTracks()[0]; }

            try {
                /* create audio context */
                newActiveSpeechEnhancement.context = new AudioContext();
                newActiveSpeechEnhancement.streamDestination = newActiveSpeechEnhancement.context.createMediaStreamDestination();
                newActiveSpeechEnhancement.source = newActiveSpeechEnhancement.context.createMediaStreamSource(newActiveSpeechEnhancement.stream);

                /* load worklet node */
                const modulePath = (config.local === true) ? "temp/speechEnhancement.min.js" : "js/speechEnhancement.min.js";
                await newActiveSpeechEnhancement.context.audioWorklet.addModule(modulePath).catch(err => {
                    this.logger.error(`[speechEnhancementService] startProcessStream failed to load module ERROR: ${err}`);
                });
                newActiveSpeechEnhancement.speechEnhancementNode = new AudioWorkletNode(newActiveSpeechEnhancement.context, 'speechEnhancement');

                /* create gain nodes */
                newActiveSpeechEnhancement.preGainNode = newActiveSpeechEnhancement.context.createGain();
                newActiveSpeechEnhancement.preGainNode.gain.value = 0.63096; // Reduce the signal by 4dB
                newActiveSpeechEnhancement.postGainNode = newActiveSpeechEnhancement.context.createGain();
                newActiveSpeechEnhancement.postGainNode.gain.value = 2.51189; // Boost the signal by 8dB

                /* create filter node */
                newActiveSpeechEnhancement.filterNode = newActiveSpeechEnhancement.context.createBiquadFilter();
                newActiveSpeechEnhancement.filterNode.type = "highpass";
                newActiveSpeechEnhancement.filterNode.frequency.setValueAtTime(150, newActiveSpeechEnhancement.context.currentTime);

                /* connect nodes */
                newActiveSpeechEnhancement.source
                    .connect(newActiveSpeechEnhancement.preGainNode)
                    .connect(newActiveSpeechEnhancement.speechEnhancementNode)
                    .connect(newActiveSpeechEnhancement.postGainNode)
                    .connect(newActiveSpeechEnhancement.filterNode)
                    .connect(newActiveSpeechEnhancement.streamDestination);


                /* replace original track with the new one */
                if (newActiveSpeechEnhancement.stream.getAudioTracks().length > 0) { newActiveSpeechEnhancement.stream.removeTrack(newActiveSpeechEnhancement.stream.getAudioTracks()[0]); }
                newActiveSpeechEnhancement.streamDestination.stream.getAudioTracks()[0].enabled = true;
                newActiveSpeechEnhancement.stream.addTrack(newActiveSpeechEnhancement.streamDestination.stream.getAudioTracks()[0]);

            } catch (error) {
                this.logger.error(`[speechEnhancementService] startProcessStream ERROR: ${error}`);

                //add original track back to the stream
                if (newActiveSpeechEnhancement.originalTrack) { // ptet remplacer que si le stream n'a pas de track
                    if (newActiveSpeechEnhancement.stream.getAudioTracks().length > 0) { newActiveSpeechEnhancement.stream.removeTrack(newActiveSpeechEnhancement.stream.getAudioTracks()[0]); }
                    newActiveSpeechEnhancement.originalTrack.enabled = true;
                    newActiveSpeechEnhancement.stream.addTrack(newActiveSpeechEnhancement.originalTrack);
                    newActiveSpeechEnhancement.originalTrack = null;
                }
            }
        }

        this.activeSpeechEnhancements[id] = newActiveSpeechEnhancement;

        return newActiveSpeechEnhancement.stream;
    }

    /**
     * @function stopProcessStream
     * @instance
     * @public
     * @async
     * @description Stop and destroy all tracks
     * @param {string} id the ID of the speech enchacement object containing the streams to be stopped
     * @param {string} serviceName The name of service that calls the method (optional, for debug purpose)
     */
    public async stopProcessStream(id: string, serviceName?: string): Promise<void> {
        if (!this.activeSpeechEnhancements[id]) return;

        this.logger.info(`[speechEnhancementService] stop noise reduction from service ${serviceName ? serviceName : 'Unknown'} for ID ${id}`);

        if (this.activeSpeechEnhancements[id]?.originalTrack) {
            this.activeSpeechEnhancements[id].originalTrack.enabled = false;
            this.activeSpeechEnhancements[id].originalTrack.stop();
            this.activeSpeechEnhancements[id].originalTrack = null;
        }

        //destroy all media tracks of this.stream and get the sream
        if (this.activeSpeechEnhancements[id]?.stream) {
            this.activeSpeechEnhancements[id].stream.getTracks().forEach((track: MediaStreamTrack) => {
                track.enabled = false;
                track.stop();
                track = null;
            });
            this.activeSpeechEnhancements[id].stream = null;
        }

        /* destroy nodes */
        if (this.activeSpeechEnhancements[id]?.speechEnhancementNode) {
            this.activeSpeechEnhancements[id].speechEnhancementNode.port.close();
            this.activeSpeechEnhancements[id].speechEnhancementNode.disconnect();
            this.activeSpeechEnhancements[id].speechEnhancementNode = null;
        }

        if (this.activeSpeechEnhancements[id]?.source) { this.activeSpeechEnhancements[id].source.disconnect(); }
        if (this.activeSpeechEnhancements[id]?.preGainNode) { this.activeSpeechEnhancements[id].preGainNode.disconnect(); }
        if (this.activeSpeechEnhancements[id]?.postGainNode) { this.activeSpeechEnhancements[id].postGainNode.disconnect(); }
        if (this.activeSpeechEnhancements[id]?.filterNode) { this.activeSpeechEnhancements[id].filterNode.disconnect(); }
        if (this.activeSpeechEnhancements[id]?.streamDestination) { this.activeSpeechEnhancements[id].streamDestination.disconnect(); }

        if (this.activeSpeechEnhancements[id]?.context) {
            await this.activeSpeechEnhancements[id].context.close();
            this.activeSpeechEnhancements[id].context = null;
        }

        delete this.activeSpeechEnhancements[id];
    }

    /****************************************************************************/
    /** SERVICE LIFECYCLE                                                      */
    /****************************************************************************/

    /**
     * @function start
     * @private
     * @async
     * @description This async method has to be call to start the speech enhancement Service
     * @returns {Promise<void>} a promise that resolve when speech enhancement service is started
     */
    public async start(): Promise<void> {
        this.logger.info(`[SpeechEnhancementService] === Start ===`);
        this.webConferenceService = this.mainService.inject(BUBBLE_CONFERENCE_SVC);
        this.webConferenceService.subscribe((event) => {
            if (event.name === BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE) {
                if (event.data.status === BubbleConferenceStatus.ENDED) {
                    if (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") { this.stopProcessStream("[webConferenceService]"); }
                }
            }
        });
    }

    /**
     * @function stop
     * @private
     * @async
     * @description This async method has to be call to stop the speech enhancement Service
     * @returns {Promise<void>} a promise that resolve when speech enhancement service is stopped
     */
    public async stop(): Promise<void> {
        this.logger.info('[SpeechEnhancementService] === Stop ===');
        Object.keys(this.activeSpeechEnhancements).forEach((key) => {
            this.stopProcessStream(key);
        });
    }

    /**
     * @function reconnect
     * @private
     * @async
     * @description This async method has to be call to reconnect the speech enhancement Service
     * @returns {Promise<void>} a promise that resolve when speech enhancement service is reconnected
     */
    public async reconnect(): Promise<void> {
        this.logger.info('[SpeechEnhancementService] === Reconnect ===');
    }
}
